import $ from "jquery";
import { autorun, observable, runInAction, when, reaction } from "mobx";
import { heroState } from "./event-hero";
import { ToastContainer, Toast } from "./toast-components";
import { popModal } from "./modal-button";

(function () {
  // Escape hatch
  if (!WP.uid || !WP.nonce) return;

  // Initialize
  const goalsState = observable({
    goals: WP.goals,
    get totalPoints() {
      // get total points
      return this.goals.reduce(
        (sum, goal) => (sum += parseInt(goal.points) || 0),
        0
      );
    },
    get completedPoints() {
      // get completed total points
      return this.goals.reduce(
        (sum, goal) =>
          (sum +=
            goal.completed && parseInt(goal.points)
              ? parseInt(goal.points)
              : 0),
        0
      );
    },
  });
  const batchedToasts = observable([]);
  const lifespan = parseInt(WP.announcements?.lifespan || 7) * 1000;

  // Goal completions get written to the activity log, so we need to set up Firebase auth
  // ? Actual Firebase login performed in visit-time.js
  // ? we just piggy back into the Firebase authStateChanged event
  let logActivity = () => {};
  firebase.auth().onAuthStateChanged(function (fb_user) {
    if (fb_user) {
      // Firebase setup
      const database = firebase.database(),
        envKey = WP.envKey,
        activityLogRef = database.ref(`${envKey}/activityLog`),
        user = parseInt(WP.uid)
          ? {
              uid: parseInt(WP.uid),
              niceName: WP.niceName,
            }
          : null;

      if (user) {
        logActivity = (data = {}) => {
          if (!user.uid || !data.type) return;

          const core = {
            user,
            timestamp: Math.floor(Date.now() / 1000),
          };
          const dataPoint = {
            ...core,
            ...data,
          };
          activityLogRef.push(dataPoint);
        };
      }
    }
  });

  // Listen for goal completions
  goalsState.goals.forEach((goal) => {
    if (!goal.completed) {
      //* Mobx one-time reaction, see:https://mobx.js.org/reactions.html#when
      when(
        () => goal.completed,
        () => {
          // Tell server we completed this goal
          fetch(`/wp-json/jumbo/v1/goal-complete/${goal.id}`, {
            method: "GET",
            headers: {
              "X-WP-Nonce": WP.nonce,
            },
          })
            .then((response) => {
              // If request was successful
              if (response.ok) {
                //!unconditional:
                showGoalContent(goal.id);
                logGoalCompletion(goal);

                //!conditional
                if (!goal.hidden) {
                  // update UI component
                  goal.isTopLevel
                    ? updateTopLevelGoalUI(goal)
                    : updateSubGoalUI(goal);
                  // add goal toast
                  if (!goal.disable_announcement) {
                    popGoalToast(goal);
                  }
                }
                if (goal.modal) {
                  popModal(goal.modal);
                }
                if (!goal.topLevel) {
                  updateTopGoalPercentage(goal.id);
                  const parentGoals = goalsState.goals.filter(
                    (topGoal) =>
                      topGoal.subGoals && topGoal.subGoals.includes(goal.id)
                  );
                  // complete the corresponding rule on all parents
                  parentGoals.forEach((g) => {
                    g.ruleGroups.forEach((rG, i) => {
                      rG.rules.forEach((r, j) => {
                        if (r.type === "goal" && r.value === goal.id) {
                          const uP = rG.usePartial && rG.usePartial > 0;
                          completeRule(g, i, rG.rules, j, uP);
                        }
                      });
                    });
                  });
                }
              }
            })
            .catch((error) => {
              // If request or processing had error, show in console
              console.error("Goal completion API error: ", error);
            });
        }
      );
    }
  });

  // Catch reaction when batchToasts length is 1
  reaction(
    () => batchedToasts.length === 1,
    () => {
      // 1s timeout lets near-simultaneous goal completions be batched
      setTimeout(() => {
        // Pop toasts here
        if (batchedToasts.length) ToastContainer(batchedToasts, lifespan);
        // empty the array
        runInAction(() => (batchedToasts.length = 0));
      }, 1000);
    }
  );

  /**
   * Updates the points label of a goal to "complete" color if it is complete
   * @param {HTMLElement} goalTopLevel
   */
  function updateGoalPointsLabelUI(goalTopLevel) {
    const topLevelPointsLabel = goalTopLevel.querySelector(
      ".goal-top-level-points"
    );
    if (topLevelPointsLabel) {
      topLevelPointsLabel.classList.add("goal-top-level-points-complete");
    }
  }

  // Sync reactive state with local storage
  autorun(() => {
    // Get toal points and completed points from goalsState object
    let totalPoints = goalsState.totalPoints;
    let completedPoints = goalsState.completedPoints;

    // If any goals have points, then display Total Points
    if (totalPoints > 0) {
      const totalPointsWrapper = document.getElementById(
        "user-goals-total-points-wrapper"
      );
      if (totalPointsWrapper) {
        totalPointsWrapper.style.display = "block";

        // Show the number of completed points
        const completedPointsLabel = totalPointsWrapper.querySelector(
          "#user-goals-total-points"
        );
        if (completedPointsLabel) {
          completedPointsLabel.innerHTML = completedPoints;
        }
      }
    }

    // Get elements with goal-top-level class
    const goalWrappers = [...document.getElementsByClassName("goal-wrapper")];
    goalWrappers.forEach((wrapper) => {
      // If goal is complete
      if (wrapper.querySelector(".goal-top-level-complete")) {
        // Remove 100%
        const goalWrapperTop = wrapper.querySelector(
          ".goal-top-level-complete"
        );
        if (
          goalWrapperTop.childNodes[3] &&
          goalWrapperTop.childNodes[3].querySelector("h3")
        ) {
          goalWrapperTop.childNodes[3].querySelector("h3").innerHTML = "";
        }
        // Update goal's points label color to "complete"
        updateGoalPointsLabelUI(goalWrapperTop);
      }

      // If goal's data completion is 100%
      if (wrapper.querySelector(".goal-top-level")) {
        const goalWrapperTop = wrapper.querySelector(".goal-top-level");
        if (
          goalWrapperTop.dataset.completion &&
          goalWrapperTop.dataset.completion === "100"
        ) {
          // Show the goal as goal-top-level-complete green color
          goalWrapperTop.classList.add("goal-top-level-complete");
          goalWrapperTop.classList.remove("goal-top-level");
        }
      }
    });
  });

  /**
   * Updates the goalsState of a completed goal
   * @param {Goal} goal
   * @param {number} i
   * @param {[Rules]} rules
   * @param {number} j
   * @param {boolean} usePartial
   */
  function completeRule(goal, i, rules, j, usePartial) {
    const thisRule = goal.ruleGroups[i].rules[j];

    // bail if already completed
    if (thisRule.completed) return;

    // mark the individual rule as complete
    runInAction(() => (thisRule.completed = true));

    // notify server
    if (thisRule.ruleId) {
      fetch(`/wp-json/jumbo/v1/rule-complete/${thisRule.ruleId}`, {
        method: "GET",
        headers: {
          "X-WP-Nonce": WP.nonce,
        },
      });
    }

    // if there is only 1 rule in the rule group
    if (rules.length === 1) {
      // mark the goal as complete
      runInAction(() => (goal.completed = true));
    }

    // Find out how many rules are complete
    let partialCompleted = 0;
    for (let k = 0; k < rules.length; k++) {
      if (rules[k].completed) partialCompleted++;
    }

    // If usePartial is enabled and number of rules completed is greather than or equal
    // Or if the number of rules completed is equal to number of rules
    if (
      (usePartial > 0 && partialCompleted >= usePartial) ||
      partialCompleted === rules.length
    ) {
      // mark the goal as complete
      runInAction(() => (goal.completed = true));
    }
  }

  // Buttons Event Listeners
  const buttons = document.querySelectorAll(".button[data-guid]");
  buttons.forEach((button) => {
    button.addEventListener("click", () => {
      // Get button GUID from dataset
      let btnGuid = button.dataset.guid.split("_")[0];
      if (!btnGuid) return;

      // Find out which goal has the button as a requirement
      goalsState.goals.forEach((goal) => {
        const ruleGroups = goal.ruleGroups;
        for (let i = 0; i < ruleGroups.length; i++) {
          const ruleGroup = ruleGroups[i];
          const usePartial = ruleGroup.usePartial;
          const rules = ruleGroup.rules;
          for (let j = 0; j < rules.length; j++) {
            const rule = rules[j];
            // If the rule value equals the button's guid
            const ruleGuid =
              typeof rule.value === "string" && rule.value.includes("_")
                ? rule.value.split("_")[1]
                : rule.value;
            if (rule.type === "button" && ruleGuid === btnGuid) {
              // complete the rule
              completeRule(goal, i, rules, j, usePartial);
            }
          }
        }
      });
    });
  });

  // Clips Event Listeners
  goalsState.goals.forEach((goal) => {
    const ruleGroups = goal.ruleGroups;
    for (let i = 0; i < ruleGroups.length; i++) {
      const ruleGroup = ruleGroups[i];
      const usePartial = ruleGroup.usePartial;
      const rules = ruleGroup.rules;
      for (let j = 0; j < rules.length; j++) {
        const rule = rules[j];
        if (rule.type === "clip") {
          // mobx when for when heroState.media.wpId equals the rule value
          when(
            () => heroState.media?.wpId == rule.value,
            () => {
              // complete the rule
              completeRule(goal, i, rules, j, usePartial);
            }
          );
          // Channel Event Listener
        } else if (rule.type === "room") {
          // If the goal rule's Channel ID matches this page ID
          if (rule.value === parseInt(WP.pageID)) {
            completeRule(goal, i, rules, j);
          }
        }
      }
    }
  });

  /**
   * Form Event Listners
   *
   * Form events come in two flavors:
   *
   * 1) AJAX-submitted forms that fire a "gform_confirmation_loaded" event
   *    on completion
   * 2) Standard http POST forms, which are configured serverside to
   *    populate the WP.completeRulesOnLoad array
   */
  $(document).on("gform_confirmation_loaded", function (event, formId) {
    goalsState.goals.forEach((goal) => {
      const ruleGroups = goal.ruleGroups;
      for (let i = 0; i < ruleGroups.length; i++) {
        const ruleGroup = ruleGroups[i];
        const usePartial = ruleGroup.usePartial;
        const rules = ruleGroup.rules;
        for (let j = 0; j < rules.length; j++) {
          const rule = rules[j];
          if (rule.type === "form" && rule.value === formId) {
            completeRule(goal, i, rules, j, usePartial);
          }
        }
      }
    });
  });

  if (WP.completeRulesOnLoad) {
    WP.completeRulesOnLoad.forEach((ruleId) => {
      goalsState.goals.forEach((goal) => {
        const ruleGroups = goal.ruleGroups;
        for (let i = 0; i < ruleGroups.length; i++) {
          const ruleGroup = ruleGroups[i];
          const usePartial = ruleGroup.usePartial;
          const rules = ruleGroup.rules;
          for (let j = 0; j < rules.length; j++) {
            const rule = rules[j];
            if (rule.type === "form" && rule.ruleId === ruleId) {
              completeRule(goal, i, rules, j, usePartial);
            }
          }
        }
      });
    });
  }

  /**
   * Updates the UI of a top-level goal
   * @param {Goal} goal
   */
  function updateTopLevelGoalUI(goal) {
    const matches = Array.from(
      document.querySelectorAll(`.goal-top-level[data-wp-id="${goal.id}"]`)
    );
    matches.forEach((match) => {
      // Update top level goal UI
      match.classList.add("goal-top-level-complete");
      match.classList.remove("goal-top-level");
      match.dataset.completion = "100";
      match.querySelector("h3").innerHTML = "";
      // Update top level goal points color to "complete"
      updateGoalPointsLabelUI(match);
    });
  }

  /**
   * Updates the UI of a sub goal
   * @param {Goal} goal
   */
  function updateSubGoalUI(goal) {
    const matches = Array.from(
      document.querySelectorAll(`.goal-sub-level[data-wp-id="${goal.id}"]`)
    );
    matches.forEach((match) => {
      match.closest(".goal-sub-level-body").classList.add("complete");
      const checkmark = match.querySelector(".goal-checkmark");
      if (checkmark) {
        checkmark.innerHTML = document.getElementById("hidden-check").innerHTML;
      }
    });
  }

  /**
   * Searches array of goals for one with specific key value
   * @param {number} id
   * @param {[Goal]} myArray
   * @returns
   */
  function search(id, arr) {
    for (var i = 0; i < arr.length; i++) {
      if (arr[i].id === id) return arr[i];
    }
  }

  /**
   * Updates the progress UI of a top level goal
   * @param {Goal} goal
   * @param {number} percentComplete
   * @returns
   */
  function updateGoalProgressUI(goal, percentComplete) {
    if (!goal.isTopLevel) return;
    const goalTopLevels = document.querySelectorAll(".goal-top-level");
    goalTopLevels.forEach((topLevel) => {
      const wpId = parseInt(topLevel.dataset.wpId);
      if (wpId === goal.id) {
        topLevel.dataset.completion = percentComplete;
        topLevel.querySelector("h3").innerHTML = `${percentComplete}% Complete`;
        if (percentComplete === 100) {
          topLevel.classList.remove("goal-top-level");
          topLevel.classList.add("goal-top-level-complete");
        }
        // Update goal top level points label color to "complete"
        updateGoalPointsLabelUI(topLevel);
      }
    });
  }

  /**
   * Updates the goal progress of a top level goal if the input is a sub goal
   * @param {Goal} goal
   */
  function updateTopGoalPercentage(goalId) {
    // Look through all top level goals
    for (let i = 0; i < goalsState.goals.length; i++) {
      const goal = goalsState.goals[i];
      // Skip sub goals
      if (!goal.isTopLevel) continue;

      // Check if the sub goal id exists in this top level goal
      if (
        goal.subGoals &&
        goal.subGoals.length &&
        goal.subGoals.includes(goalId)
      ) {
        // Find out completion percentage
        let subGoalsComplete = 0;
        // Loop through sub goals and see how many completed
        goal.subGoals.forEach((subGoal) => {
          const sub = search(subGoal, goalsState.goals);
          if (sub && sub.completed) subGoalsComplete++;
        });
        const percentComplete = Math.ceil(
          (subGoalsComplete / goal.subGoals.length) * 100
        );
        updateGoalProgressUI(goal, percentComplete);

        // all subGoals complete: complete top level goal
        if (subGoalsComplete === goal.subGoals.length) {
          runInAction(() => (goal.completed = true));
        }
      }
    }
  }

  /**
   * Adds goal to batched toasts array
   * @param {Goal} goal
   */
  function popGoalToast(goal) {
    let link = "";
    if (goal.location) {
      link = goal.location;
      if (goal.url_ext) link += goal.url_ext;
    } else {
      link = location.origin + location.pathname;
      if (goal.url_ext) {
        link += goal.url_ext;
      } else {
        link += "#";
        if (document.querySelector('[data-tab-type="goals"]')) {
          link += document.querySelector('[data-tab-type="goals"]').id;
        }
      }
    }
    // Create a new toast object
    let NewToast = Toast({
      text: `${goal.title} completed!`,
      icon: goal.icon || WP.defaultGoalCompleteIcon,
      link,
      target: "",
    });
    runInAction(() => batchedToasts.push(NewToast));
  }

  /**
   * Logs goal completion to firebase
   * @param {Goal} goal
   */
  function logGoalCompletion(goal) {
    // log to activity log
    logActivity({
      type: "goalComplete",
      goal,
    });
  }

  /**
   * Display the associated goal content row
   * @param {number} goalId
   */
  function showGoalContent(goalId) {
    // Find the goal content section and nav item
    const section = document.querySelector(`section[data-goal-id="${goalId}"]`);
    if (section) section.dataset.goalCompleted = "1";
    const nav = document.querySelector(`li[data-goal-id="${goalId}"]`);
    if (nav) nav.style.display = "inline-block";
  }
})();
